import { toValue, watch, type MaybeRefOrGetter } from "vue";

export interface CustomGoogleFont {
  family: string;
  weights?: number[];
}

function createGoogleFontLink({
  family: fontName,
  weights: fontWeights,
}: CustomGoogleFont) {
  const link = document.createElement("link");
  link.setAttribute("rel", "stylesheet");
  link.setAttribute("type", "text/css");

  const url = new URL("https://fonts.googleapis.com/css");

  let fontWeightsParam = "";
  if (fontWeights && fontWeights.length > 0) {
    fontWeightsParam = `:${fontWeights.join(",")}`;
  }

  url.searchParams.set("family", `${fontName}${fontWeightsParam}`);

  link.setAttribute("href", url.toString());

  return link;
}

export function useGoogleFont(
  definition: MaybeRefOrGetter<CustomGoogleFont | null>,
  cssClassName?: string,
) {
  function setCssDeclaration(
    definition: CustomGoogleFont,
    cssClassName: string,
  ) {
    const newFont = definition.family;

    // Create a temporary element to get the current font-family for the class
    const tempEl = document.createElement("div");
    tempEl.className = cssClassName;
    document.body.appendChild(tempEl);
    const currentValue = getComputedStyle(tempEl).fontFamily;
    document.body.removeChild(tempEl);

    // Create a style element with the new font prepended
    const style = document.createElement("style");
    const fontStack = currentValue.includes(newFont)
      ? currentValue
      : `"${newFont}", ${currentValue}`;

    style.textContent = `.${cssClassName} { font-family: ${fontStack}; }`;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }

  watch(
    () => toValue(definition),
    (font, _, onCleanup) => {
      if (!font) return;

      const link = createGoogleFontLink(font);
      document.head.appendChild(link);

      const removeCssDeclaration = cssClassName
        ? setCssDeclaration(font, cssClassName)
        : undefined;

      onCleanup(() => {
        document.head.removeChild(link);
        if (removeCssDeclaration) removeCssDeclaration();
      });
    },
    { immediate: true },
  );
}
